'use client';

import React, { useEffect, useRef, useState } from 'react';

const visaData = [
  { country: 'Germany', flag: '🇩🇪', rate: 87, note: 'Strong DAAD support', color: 'var(--sumi)' },
  { country: 'Japan', flag: '🇯🇵', rate: 79, note: 'CoE required, 6-8 wks', color: 'var(--sumi)' },
  { country: 'France', flag: '🇫🇷', rate: 82, note: 'Campus France interview', color: 'var(--sumi)' },
  { country: 'Australia', flag: '🇦🇺', rate: 91, note: 'Fastest processing avg.', color: 'var(--vermillion)' },
  { country: 'UK', flag: '🇬🇧', rate: 74, note: 'CAS letter delays common', color: 'var(--sumi)' },
  { country: 'South Korea', flag: '🇰🇷', rate: 85, note: 'D-2 student visa, 3 wks', color: 'var(--sumi)' },
  { country: 'Netherlands', flag: '🇳🇱', rate: 88, note: 'MVV required for non-EU', color: 'var(--sumi)' },
];

export default function VisaPanel() {
  const [animated, setAnimated] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setAnimated(true), 300);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="scroll-panel" style={{ background: 'var(--washi)' }}>
      <div className="noise-overlay" />
      <div className="w-full h-full flex flex-col md:flex-row items-stretch pt-16 pb-12">

        {/* LEFT: Chapter label + stat headline */}
        <div
          className="flex-shrink-0 md:w-[38%] flex flex-col justify-between px-8 md:px-12 py-8 md:py-10"
          style={{ borderRight: '1px solid rgba(184,175,166,0.4)' }}
        >
          <div className="panel-content">
            <div className="section-label mb-4">Chapter 01 — Visas</div>
            <hr className="tatami-rule mb-8" />

            <h2
              className="font-fraunces font-medium mb-6"
              style={{ fontSize: 'clamp(2rem, 3.5vw, 3.2rem)', color: 'var(--sumi)', letterSpacing: '-0.02em', lineHeight: 1.1 }}
            >
              1 in 4 applications<br />
              <span style={{ fontStyle: 'italic', color: 'var(--vermillion)' }}>stall at the same step.</span>
            </h2>

            <p className="font-dm-sans mb-8" style={{ fontSize: '14px', color: 'var(--sumi-light)', lineHeight: 1.7, fontWeight: 300, maxWidth: '320px' }}>
              Approval rates vary by 17 percentage points across the most popular student destinations. The difference is almost never the applicant — it's the supporting documents.
            </p>

            {/* Handwritten annotation */}
            <div style={{ background: '#fffef9', border: '1px solid rgba(184,175,166,0.3)', padding: '12px 16px', transform: 'rotate(-1deg)', maxWidth: '280px' }}>
              <p className="font-fraunces" style={{ fontSize: '13px', fontStyle: 'italic', color: 'var(--sumi)', lineHeight: 1.6 }}>
                "My visa was rejected twice because of a bank statement format. Nobody told me."
              </p>
              <div className="section-label mt-2" style={{ color: 'var(--vermillion)' }}>— James O., Lagos → Amsterdam</div>
            </div>
          </div>

          {/* Bottom meta */}
          <div className="section-label mt-8" style={{ color: 'var(--tatami)' }}>
            Data: 11,247 applications · 2023–2025
          </div>
        </div>

        {/* RIGHT: Data visualization */}
        <div ref={ref} className="flex-1 flex flex-col justify-center px-8 md:px-12 py-8 md:py-10 overflow-y-auto">
          <div className="panel-content">
            <div className="flex items-center justify-between mb-6">
              <div className="section-label">Student visa approval rates (%)</div>
              <div className="section-label" style={{ color: 'var(--vermillion)' }}>
                ▲ Highest: Australia
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
              {visaData?.map((item, i) => (
                <div key={item?.country}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '18px' }}>{item?.flag}</span>
                      <span className="font-dm-sans" style={{ fontSize: '13px', fontWeight: 500, color: 'var(--sumi)' }}>{item?.country}</span>
                      <span className="font-plex-mono" style={{ fontSize: '10px', color: 'var(--tatami)', letterSpacing: '0.05em' }}>{item?.note}</span>
                    </div>
                    <span
                      className="font-fraunces"
                      style={{ fontSize: '20px', fontWeight: 600, color: item?.rate > 88 ? 'var(--vermillion)' : 'var(--sumi)', letterSpacing: '-0.02em' }}
                    >
                      {item?.rate}%
                    </span>
                  </div>
                  {/* Bar */}
                  <div style={{ height: '5px', background: 'rgba(184,175,166,0.3)', position: 'relative', overflow: 'hidden' }}>
                    <div
                      style={{
                        height: '100%',
                        background: item?.rate > 88 ? 'var(--vermillion)' : 'var(--sumi)',
                        width: animated ? `${item?.rate}%` : '0%',
                        transition: `width ${0.8 + i * 0.1}s cubic-bezier(0.19, 1, 0.22, 1) ${i * 0.08}s`,
                        opacity: 0.85,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>

            <hr className="tatami-rule mt-8 mb-4" />
            <div className="flex items-center gap-6">
              <div className="section-label">Source: Elite Consultancy Research Unit, Feb 2026</div>
              <div style={{ display: 'flex', gap: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{ width: '12px', height: '4px', background: 'var(--sumi)', opacity: 0.7 }} />
                  <span className="section-label">Standard</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{ width: '12px', height: '4px', background: 'var(--vermillion)' }} />
                  <span className="section-label" style={{ color: 'var(--vermillion)' }}>Highest rate</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}